import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { engagementApi, googleAdsApi, linkedInAdsApi, marketingApi, openAIAdsApi, xAdsApi } from '../services/api';
import { BySource, MetaCampaignSpend, SourceFunnel } from '../types';
import { fmtINR, istToday } from '../lib';
import DateRangeBar from '../components/DateRangeBar';
import NumberStrip, { StripItem } from '../components/NumberStrip';
import PlatformsCard, { PlatformSpend, USD_INR } from '../components/PlatformsCard';
import SourcesList from '../components/SourcesList';
import { btn, btnTone, Section, Failed, Loading, PageFrame, Strip, Th, td, tdBig, tr, Dash } from '../components/PlatformBits';

/** Sources first, site visits first (§0): what each arrival source put in and
 *  what came out — visitors, leads, site visits, and spend from every platform
 *  we sync (Meta, X, Google, LinkedIn, ChatGPT). Per-source spend joins on the UTM campaign id
 *  (Meta); the Platforms card carries every platform's total against the
 *  stages its leads reached. */
const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState({ from: istToday(), to: istToday() });
  const [data, setData] = useState<BySource | null>(null);
  const [spend, setSpend] = useState<MetaCampaignSpend[] | null>(null);
  const [xSpend, setXSpend] = useState<number | null>(null);        // ₹, converted
  const [googleSpend, setGoogleSpend] = useState<number | null>(null);
  const [linkedInSpend, setLinkedInSpend] = useState<number | null>(null);
  const [chatGPTSpend, setChatGPTSpend] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    setData(null);
    setError(null);
    engagementApi.bySource(range.from, range.to).then(setData)
      .catch((err: any) => setError(err?.response?.data?.detail || err.message));
    // Spend is additive context — the page renders before it lands — but a
    // failure must show, not read as "no spend" (a truncated key hid a 401
    // here for six days).
    setSpend(null);
    marketingApi.campaignSpend(range.from, range.to).then((r) => setSpend(r.campaigns))
      .catch((err: any) => setError(`Meta spend failed: ${err?.response?.data?.detail || err.message}`));
    setXSpend(null); setGoogleSpend(null); setLinkedInSpend(null); setChatGPTSpend(null);
    xAdsApi.report(range.from, range.to).then((r) => setXSpend(r.posts.reduce((a, p) => a + p.spend_micro, 0) / 1e6 * USD_INR))
      .catch((err: any) => setError(`X spend failed: ${err?.response?.data?.detail || err.message}`));
    googleAdsApi.report(range.from, range.to).then((r) => setGoogleSpend(r.campaigns.reduce((a, c) => a + c.cost_micros, 0) / 1e6))
      .catch((err: any) => setError(`Google spend failed: ${err?.response?.data?.detail || err.message}`));
    linkedInAdsApi.report(range.from, range.to).then((r) => setLinkedInSpend(r.campaigns.reduce((a, c) => a + c.cost, 0)))
      .catch((err: any) => setError(`LinkedIn spend failed: ${err?.response?.data?.detail || err.message}`));
    openAIAdsApi.report(range.from, range.to).then((r) => setChatGPTSpend(r.campaigns.reduce((a, c) => a + c.spend, 0)))
      .catch((err: any) => setError(`ChatGPT spend failed: ${err?.response?.data?.detail || err.message}`));
  }, [range, reloads]);

  // Each platform page's own refresh, all fired at once. A platform that fails
  // is named beside the button; the rest still reload.
  const refreshAll = () => {
    setRefreshing(true);
    setRefreshFailed(null);
    const platforms: [string, () => Promise<void>][] = [
      ['Meta', marketingApi.refreshInsights], ['X', xAdsApi.refresh],
      ['Google', googleAdsApi.refresh], ['LinkedIn', linkedInAdsApi.refresh],
      ['ChatGPT', openAIAdsApi.refresh],
    ];
    Promise.allSettled(platforms.map(([, run]) => run())).then((results) => {
      const failed = results.flatMap((r, i) => r.status === 'rejected'
        ? [`${platforms[i][0]} (${r.reason?.response?.data?.detail || r.reason?.message})`] : []);
      setRefreshFailed(failed.length ? `Refresh failed: ${failed.join(', ')}` : null);
      setRefreshing(false);
      setReloads((n) => n + 1);
    });
  };

  if (error) return <Failed what="sources" error={error} />;

  const spendByCampaign = new Map((spend ?? []).map((c) => [c.campaign_id, c]));
  const campaignByAd = new Map((spend ?? []).flatMap((c) => c.ads.map((ad) => [ad, c] as const)));
  const sources = data?.sources ?? [];
  // A tagged link is priced at its campaign, a CTWA arrival at its own ad.
  const rowSpend = (s: SourceFunnel): number | null => {
    if (s.campaign && spendByCampaign.has(s.campaign)) return spendByCampaign.get(s.campaign)!.spend;
    const byAd = s.source_kind === 'ad' && s.source_ref ? campaignByAd.get(s.source_ref) : undefined;
    return byAd ? byAd.ad_spend[s.source_ref!] ?? null : null;
  };
  // Meta's own name for the row: a tagged link by its campaign id, a CTWA
  // arrival (`a:<ad_id>`) by the campaign that ad belongs to.
  const rowLabel = (s: SourceFunnel): string | undefined => {
    if (s.campaign && spendByCampaign.has(s.campaign)) return spendByCampaign.get(s.campaign)!.campaign_name;
    const byAd = s.source_kind === 'ad' && s.source_ref ? campaignByAd.get(s.source_ref) : undefined;
    return byAd ? `${byAd.campaign_name} · ad …${s.source_ref!.slice(-5)}` : undefined;
  };
  const anyVisits = sources.some((s) => s.visits_scheduled > 0);
  // Meta campaigns that spent in the window but no source row traces to —
  // neither a tagged link (campaign id) nor an ad arrival (`a:<ad_id>`). Money
  // must not disappear from the page just because nobody arrived.
  const matched = new Set([
    ...sources.map((s) => s.campaign),
    ...sources.map((s) => (s.source_kind === 'ad' && s.source_ref ? campaignByAd.get(s.source_ref)?.campaign_id : undefined)),
  ].filter(Boolean));
  const offSite = (spend ?? []).filter((c) => !matched.has(c.campaign_id) && c.spend > 0);
  const metaSpend = (spend ?? []).reduce((a, c) => a + c.spend, 0);
  const platforms: PlatformSpend[] = [
    { key: 'meta', spend: metaSpend, synced: spend !== null },
    { key: 'x', spend: xSpend ?? 0, synced: xSpend !== null },
    { key: 'google', spend: googleSpend ?? 0, synced: googleSpend !== null },
    { key: 'linkedin', spend: linkedInSpend ?? 0, synced: linkedInSpend !== null },
    { key: 'chatgpt', spend: chatGPTSpend ?? 0, synced: chatGPTSpend !== null },
  ];
  const totalSpend = platforms.reduce((a, p) => a + p.spend, 0);

  const strip: StripItem[] = data ? [
    { v: data.totals.visits_conducted, k: 'site visits', cls: 'visited' },
    { v: data.totals.visits_scheduled, k: 'scheduled', cls: 'gold' },
    { v: data.totals.new_leads, k: 'new leads, our funnel' },
    { v: data.totals.visitors, k: 'visitors' },
    ...(totalSpend > 0 ? [{ v: fmtINR(totalSpend), k: 'ad spend' }] : []),
    ...(totalSpend > 0 && data.totals.new_leads > 0 ? [{ v: fmtINR(totalSpend / data.totals.new_leads), k: 'per lead' }] : []),
    ...(totalSpend > 0 && data.totals.visits_conducted > 0 ? [{ v: fmtINR(totalSpend / data.totals.visits_conducted), k: 'per site visit' }] : []),
  ] : [];

  return (
    <PageFrame
      title="Dashboard"
      subline={refreshFailed
        ? <span className="text-brick">{refreshFailed}</span>
        : 'Site visits, and what every source and platform spent to get them'}
      action={<button onClick={refreshAll} disabled={refreshing} className={`${btn} ${btnTone.charcoal}`}>
        {refreshing && <span className="w-3 h-3 rounded-full border-2 border-sage border-t-forest animate-spin" />}
        {refreshing ? 'Refreshing…' : 'Refresh all platforms'}
      </button>}
      bar={<DateRangeBar from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />}>
      {!data && <Loading what="sources" />}

      {data && (
        <>
          <Strip><NumberStrip items={strip} /></Strip>

          <PlatformsCard platforms={platforms} sources={sources} crmLeads={data.totals.crm_leads} />

          <SourcesList
            title="Sources"
            note="open a platform for its sources · click a source to see its people"
            collapsible
            rows={sources}
            people={(s) => s.people}
            peopleHeader="Visitors"
            tapped={(s) => s.cta_clicked}
            extra={anyVisits ? [
              { header: 'Site visits', of: (s) => s.visits_conducted, ours: true },
              { header: 'Scheduled', of: (s) => s.visits_scheduled, ours: true },
            ] : []}
            label={rowLabel}
            spendOf={rowSpend}
            onSelect={(r) => r?.tag && navigate(`/engagement?source=${encodeURIComponent(r.tag)}&from=${range.from}&to=${range.to}`)}
          />

          {offSite.length > 0 && (
            <Section title="Ad spend nobody arrived from" note="no visitor or lead in the window traces to these campaigns">
              <table className="w-full min-w-[560px] border-collapse">
                <thead><tr>
                  <Th left>Meta campaign</Th><Th left>Objective</Th><Th>WhatsApp conversations</Th><Th>Spend</Th>
                </tr></thead>
                <tbody>
                  {offSite.map((c) => (
                    <tr key={c.campaign_id} className={tr}>
                      <td className="px-3 py-3 whitespace-nowrap text-[13px] font-semibold">{c.campaign_name}</td>
                      <td className="px-3 py-3 whitespace-nowrap text-[12px] text-graphite">{c.objective?.replace('OUTCOME_', '').toLowerCase()}</td>
                      <td className={`${td} text-right`}>{c.conversations > 0 ? c.conversations.toLocaleString('en-IN') : <Dash />}</td>
                      <td className={tdBig}>{fmtINR(c.spend)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}
        </>
      )}
    </PageFrame>
  );
};

export default DashboardPage;
